import { observer } from 'mobx-react-lite'
import { EditPencil, Plus as PlusIcon, Trash } from 'iconoir-react'
import Combo from 'app/components/ui/combo/combo'
import { Button } from 'app/components/ui/button'
import { PageScroll } from 'app/components/layout/PageScroll'
import { Search } from 'app/components/Search'
import { TableBody, TableCell, TableRow } from 'app/components/ui/table'
import {
  GROUPED_TABLE_CELL,
  GroupedTable,
  GroupedTableHeaderCard,
  GroupedTableSection,
  type GroupedTableColumn,
} from 'app/components/table/GroupedTable'
import { Tooltip } from 'app/components/Tooltip'
import { cn } from 'app/lib/utils'
import { formFieldOptions } from 'lib/formFieldDefaults'
import { OptionType } from 'types/OptionType'
import {
  getFormFieldLabel,
  getUnmappedFrequency,
  type FormFieldMappingRow,
  type UnmappedFormKeyRow,
  type UseFormFieldMappingReturn,
} from './useFormFieldMapping'

export type FormFieldMappingPageShadcnComponentProps = UseFormFieldMappingReturn

const formFieldComboOptions: OptionType[] = formFieldOptions.map((opt) => ({
  value: opt.value,
  id: opt.value,
  name: opt.label,
}))

function formColumns(canEditMapping: boolean): GroupedTableColumn[] {
  return [
    { label: `Raw Key` },
    { label: `Sources`, colClassName: `w-[20%]` },
    { label: `Frequency`, colClassName: `w-[110px]` },
    { label: `Mapped To`, colClassName: `w-[25%]` },
    ...(canEditMapping
      ? [{ label: `Actions`, colClassName: `w-[100px]`, headClassName: `text-right` } as GroupedTableColumn]
      : []),
  ]
}

// Text cells that may hold long raw keys / vendor lists.
const WRAP_CELL = `whitespace-normal wrap-break-word ${GROUPED_TABLE_CELL}`
// The in-cell combo carries its own p-2, so the cell pads down to keep the
// combo text aligned with the px-5 column header.
const COMBO_CELL = `px-3 py-1.5`

function FieldCombo({ value, onChange }: { value?: string; onChange: (formField: string) => void }) {
  return (
    <Combo
      inputClassName="!h-full !border-0 !shadow-none !p-2 text-body-xs"
      placeholder="Select field..."
      options={formFieldComboOptions}
      value={value ? { value, id: value, name: getFormFieldLabel(value) } : undefined}
      onChange={(option: OptionType) => onChange(option.value as string)}
    />
  )
}

function UnmappedRow({
  row,
  canEditMapping,
  onMapField,
}: {
  row: UnmappedFormKeyRow
  canEditMapping: boolean
  onMapField: (unmappedId: string, rawKey: string, formField: string) => void
}) {
  return (
    <TableRow>
      <TableCell className={cn(WRAP_CELL, `text-strong`)}>{row.rawKey}</TableCell>
      <TableCell className={cn(WRAP_CELL, `text-default`)}>{Object.keys(row.vendors ?? {}).join(`, `)}</TableCell>
      <TableCell className={cn(GROUPED_TABLE_CELL, `tabular-nums text-default`)}>{getUnmappedFrequency(row)}</TableCell>
      {canEditMapping ? (
        <TableCell className={COMBO_CELL}>
          <FieldCombo onChange={(formField) => onMapField(row.id, row.rawKey, formField)} />
        </TableCell>
      ) : (
        <TableCell className={cn(GROUPED_TABLE_CELL, `text-minimal`)}>—</TableCell>
      )}
      {canEditMapping && <TableCell className={GROUPED_TABLE_CELL} />}
    </TableRow>
  )
}

function MappingRow({
  mapping,
  canEditMapping,
  onRemapField,
  onEditMapping,
  onDeleteMapping,
}: {
  mapping: FormFieldMappingRow
  canEditMapping: boolean
  onRemapField: (mapping: FormFieldMappingRow, formField: string) => void
  onEditMapping: (mapping: FormFieldMappingRow) => void
  onDeleteMapping: (id: string) => void
}) {
  const editable = canEditMapping && !mapping.isDefault
  return (
    <TableRow>
      <TableCell className={cn(WRAP_CELL, `text-strong`)}>{mapping.rawKey}</TableCell>
      <TableCell className={cn(GROUPED_TABLE_CELL, `text-minimal`)}>—</TableCell>
      <TableCell className={cn(GROUPED_TABLE_CELL, `text-minimal`)}>—</TableCell>
      {editable ? (
        <TableCell className={COMBO_CELL}>
          <FieldCombo value={mapping.formField} onChange={(formField) => onRemapField(mapping, formField)} />
        </TableCell>
      ) : (
        <TableCell className={cn(GROUPED_TABLE_CELL, `text-default`)}>{getFormFieldLabel(mapping.formField)}</TableCell>
      )}
      {canEditMapping && (
        <TableCell className={GROUPED_TABLE_CELL}>
          {!mapping.isDefault && (
            <div className="flex justify-end gap-2">
              <Tooltip message="Edit mapping">
                <EditPencil
                  className="aspect-square w-5 cursor-pointer text-default"
                  onClick={() => onEditMapping(mapping)}
                />
              </Tooltip>
              <Tooltip message="Delete mapping">
                <Trash
                  className="aspect-square w-5 cursor-pointer text-default"
                  onClick={() => onDeleteMapping(mapping.id!)}
                />
              </Tooltip>
            </div>
          )}
        </TableCell>
      )}
    </TableRow>
  )
}

export const FormFieldMappingPageShadcnComponent = observer(function FormFieldMappingPageShadcnComponent({
  unmappedRows,
  customMappings,
  defaultMappings,
  canEditMapping,
  onMapField,
  onRemapField,
  onEditMapping,
  onDeleteMapping,
  onNewMapping,
}: FormFieldMappingPageShadcnComponentProps) {
  const columns = formColumns(canEditMapping)
  const hasRows = unmappedRows.length > 0 || customMappings.length > 0 || defaultMappings.length > 0

  const mappingRowProps = { canEditMapping, onRemapField, onEditMapping, onDeleteMapping }

  return (
    // Top padding lives on the content, not the scroller: padding on the scroll
    // container would inset the sticky header card and let rows peek above it.
    <PageScroll contentClassName="pb-6">
      <div className="mb-0.5 flex items-center gap-x-2 pt-6">
        <Search />
        {canEditMapping && (
          <Button variant="primary" size="sm" onClick={onNewMapping}>
            <PlusIcon className="h-4 w-4" strokeWidth={1.5} />
            <span>New Mapping</span>
          </Button>
        )}
      </div>

      <div className="mt-6 flex min-w-[640px] flex-col gap-5">
        {!hasRows ? (
          <div className="py-5 text-center text-subtle">No fields found</div>
        ) : (
          <>
            <GroupedTableHeaderCard columns={columns} />
            {unmappedRows.length > 0 && (
              <GroupedTableSection label="Needs Mapping" count={unmappedRows.length} dotClassName="bg-yellow-500">
                <GroupedTable columns={columns}>
                  <TableBody className="divide-y divide-subtle">
                    {unmappedRows.map((row) => (
                      <UnmappedRow key={row.id} row={row} canEditMapping={canEditMapping} onMapField={onMapField} />
                    ))}
                  </TableBody>
                </GroupedTable>
              </GroupedTableSection>
            )}
            {customMappings.length > 0 && (
              <GroupedTableSection label="Mapped" count={customMappings.length} dotClassName="bg-green-500">
                <GroupedTable columns={columns}>
                  <TableBody className="divide-y divide-subtle">
                    {customMappings.map((mapping) => (
                      <MappingRow key={mapping.id ?? mapping.rawKey} mapping={mapping} {...mappingRowProps} />
                    ))}
                  </TableBody>
                </GroupedTable>
              </GroupedTableSection>
            )}
            {defaultMappings.length > 0 && (
              <GroupedTableSection
                label="Defaults"
                count={defaultMappings.length}
                dotClassName="bg-(--line-strong)"
                defaultOpen={false}
              >
                <GroupedTable columns={columns}>
                  <TableBody className="divide-y divide-subtle">
                    {defaultMappings.map((mapping) => (
                      <MappingRow key={mapping.rawKey} mapping={mapping} {...mappingRowProps} />
                    ))}
                  </TableBody>
                </GroupedTable>
              </GroupedTableSection>
            )}
          </>
        )}
      </div>
    </PageScroll>
  )
})
